import { useState } from 'react';
import { Link } from 'react-router-dom';
import { videos, categories, siteConfig } from '@/mocks/videos';
import VideoCard from '@/components/feature/VideoCard';

export default function Home() {
  const [activeTab, setActiveTab] = useState('all');

  const hotVideos = videos.filter((v) => v.isHot).slice(0, 8);
  const newVideos = videos.filter((v) => v.isNew);
  const featuredVideos = videos.filter((v) => v.rating >= 9.0).slice(0, 6);

  const tabFilters = [
    { key: 'all', label: '全部' },
    { key: 'movie', label: '电影' },
    { key: 'drama', label: '电视剧' },
    { key: 'variety', label: '综艺' },
    { key: 'mv', label: 'MV' },
  ];

  const filteredVideos = activeTab === 'all'
    ? videos
    : videos.filter((v) => v.category === activeTab);

  return (
    <main>
      {/* Hero Section */}
      <section className="relative w-full h-[520px] md:h-[640px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Cinematic%20wide%20shot%20of%20modern%20theater%20screen%20with%20ambient%20red%20and%20gold%20lighting%2C%20dramatic%20atmosphere%2C%20luxurious%20velvet%20curtains%2C%20premium%20cinema%20experience%2C%20dark%20rich%20tones%2C%20elegant%20entertainment%20venue%2C%20warm%20spotlight%20effects&width=1920&height=1080&seq=hero-bg-01&orientation=landscape"
          alt="日韩视频精选合集"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/70"></div>

        <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-4 md:px-6 text-center">
          <span className="inline-block px-4 py-1.5 rounded-full bg-primary-500/20 backdrop-blur-sm border border-primary-500/30 text-primary-200 text-sm font-medium mb-6">
            {siteConfig.slogan}
          </span>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-4 leading-tight max-w-3xl">
            日韩视频精选合集
            <span className="block text-lg md:text-xl font-normal text-white/80 mt-3 font-body">
              日韩电影 · 电视剧 · 综艺 · MV 高清在线观看
            </span>
          </h1>
          <p className="text-white/70 text-sm md:text-base max-w-2xl mb-8 leading-relaxed">
            涵盖日韩电影、日韩电视剧、日韩综艺节目和日韩音乐MV等高清内容。每日更新日韩视频资源，支持在线流畅播放，为日韩文化爱好者提供一站式观看体验，发现更多精彩日韩影视娱乐内容。
          </p>
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <Link
              to="/category/movie"
              className="px-8 py-3 rounded-full bg-primary-500 hover:bg-primary-600 text-white font-semibold text-sm transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              立即探索日韩视频
            </Link>
            <Link
              to="/about"
              className="px-8 py-3 rounded-full border border-white/30 hover:border-white/60 text-white font-medium text-sm transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              了解更多
            </Link>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background-50 to-transparent"></div>
      </section>

      {/* Categories Section */}
      <section className="w-full px-4 md:px-6 py-12 md:py-16">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
              日韩视频分类导航
            </h2>
            <p className="text-foreground-600 text-sm max-w-xl mx-auto">
              精选日韩电影、日韩电视剧、日韩综艺、日韩MV四大类目，满足不同日韩文化爱好者的观看需求
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="group relative overflow-hidden rounded-xl bg-background-100 p-6 hover:bg-primary-50 transition-all duration-300 cursor-pointer"
              >
                <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center mb-4 group-hover:bg-primary-500 transition-colors duration-300">
                  <i className={`${cat.icon} text-xl text-primary-500 group-hover:text-white transition-colors duration-300`}></i>
                </div>
                <h3 className="font-semibold text-foreground-950 mb-1 text-base">{cat.label}</h3>
                <p className="text-xs text-foreground-500">{cat.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Section */}
      <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                精选日韩视频推荐
              </h2>
              <p className="text-foreground-600 text-sm">高评分日韩影视内容，品质之选</p>
            </div>
            <Link
              to="/category/movie"
              className="hidden sm:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium cursor-pointer whitespace-nowrap"
            >
              查看全部 <i className="ri-arrow-right-line text-sm w-4 h-4 flex items-center justify-center"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {featuredVideos.map((video, idx) => (
              <VideoCard key={video.id} video={video} size={idx === 0 ? 'large' : 'normal'} />
            ))}
          </div>
          <div className="mt-6 text-center sm:hidden">
            <Link
              to="/category/movie"
              className="inline-flex items-center gap-1 text-sm text-primary-500 font-medium cursor-pointer"
            >
              查看全部 <i className="ri-arrow-right-line text-sm w-4 h-4 flex items-center justify-center"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Hot Section with Tabs */}
      <section className="w-full px-4 md:px-6 py-12 md:py-16">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                热门日韩视频
              </h2>
              <p className="text-foreground-600 text-sm">日韩视频爱好者都在看的热门内容</p>
            </div>
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              {tabFilters.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    activeTab === tab.key
                      ? 'bg-primary-500 text-white'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredVideos.slice(0, 8).map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>
          {filteredVideos.length > 8 && (
            <div className="mt-6 text-center">
              <Link
                to={`/category/${activeTab === 'all' ? 'movie' : activeTab}`}
                className="inline-flex items-center gap-1 px-6 py-2.5 rounded-full border border-background-300 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-500 transition-all cursor-pointer whitespace-nowrap"
              >
                查看更多日韩视频 <i className="ri-arrow-down-line text-sm w-4 h-4 flex items-center justify-center"></i>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Latest Updates */}
      {newVideos.length > 0 && (
        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                  最新日韩视频更新
                </h2>
                <p className="text-foreground-600 text-sm">每日更新日韩视频，精彩不容错过</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {newVideos.map((video) => (
                <div key={video.id} className="flex gap-4 bg-background-50 rounded-xl p-4 group hover:bg-primary-50/50 transition-colors duration-300 cursor-pointer">
                  <Link to={`/article/${video.id}`} className="flex-shrink-0 w-40 h-24 rounded-lg overflow-hidden">
                    <img
                      src={video.thumbnail}
                      alt={`${video.title} - ${video.categoryLabel}`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <span className="text-xs px-2 py-0.5 rounded bg-primary-100 text-primary-600 font-medium">{video.categoryLabel}</span>
                    <Link to={`/article/${video.id}`} className="block mt-1.5">
                      <h3 className="font-semibold text-sm text-foreground-900 line-clamp-2 hover:text-primary-500 transition-colors">{video.title}</h3>
                    </Link>
                    <div className="flex items-center gap-2 mt-1.5 text-xs text-foreground-500">
                      <span>{video.region}</span>
                      <span>{video.year}</span>
                      <span className="flex items-center gap-0.5">
                        <i className="ri-star-fill text-accent-500 text-xs w-3 h-3 flex items-center justify-center"></i>
                        {video.rating}
                      </span>
                    </div>
                    <p className="text-xs text-foreground-600 mt-1.5 line-clamp-1">{video.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Stats & CTA */}
      <section className="w-full px-4 md:px-6 py-16 md:py-20 relative overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Elegant%20minimal%20dark%20background%20with%20soft%20red%20and%20amber%20gradient%20light%20streaks%2C%20abstract%20cinematic%20atmosphere%2C%20luxurious%20texture%2C%20warm%20ambient%20glow%2C%20premium%20entertainment%20backdrop&width=1920&height=600&seq=cta-bg-01&orientation=landscape"
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-foreground-950/85"></div>
        <div className="relative z-10 max-w-[1400px] mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4">
            加入日韩视频爱好者社区
          </h2>
          <p className="text-white/70 text-sm max-w-lg mx-auto mb-8">
            每日更新海量日韩视频资源，日韩电影、日韩电视剧、日韩综艺、日韩MV应有尽有，立即开启您的日韩视频之旅
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <div className="text-center px-8">
              <div className="text-3xl font-bold text-primary-400 font-heading">500万+</div>
              <div className="text-xs text-white/50 mt-1">日韩视频资源</div>
            </div>
            <div className="hidden sm:block w-px h-10 bg-white/10"></div>
            <div className="text-center px-8">
              <div className="text-3xl font-bold text-accent-400 font-heading">200万+</div>
              <div className="text-xs text-white/50 mt-1">日韩视频爱好者</div>
            </div>
            <div className="hidden sm:block w-px h-10 bg-white/10"></div>
            <div className="text-center px-8">
              <div className="text-3xl font-bold text-secondary-400 font-heading">98%</div>
              <div className="text-xs text-white/50 mt-1">用户满意度</div>
            </div>
          </div>
          <div className="mt-8">
            <Link
              to="/category/movie"
              className="inline-block px-8 py-3 rounded-full bg-primary-500 hover:bg-primary-600 text-white font-semibold text-sm transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              开始观看日韩视频
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="w-full px-4 md:px-6 py-12 md:py-16">
        <div className="max-w-[900px] mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
              日韩视频常见问题
            </h2>
            <p className="text-foreground-600 text-sm">关于日韩视频精选合集的常见疑问解答</p>
          </div>
          <div className="space-y-3">
            {[
              { q: '日韩视频精选合集提供哪些类型的视频内容？', a: '我们提供日韩电影、日韩电视剧、日韩综艺节目、日韩音乐MV等丰富的高清视频内容，涵盖最新的日本和韩国影视娱乐资源。' },
              { q: '日韩视频的更新频率是怎样的？', a: '我们的内容团队每日更新日韩视频资源，确保用户能够第一时间观看到最新的日韩电影、电视剧、综艺和MV内容。' },
              { q: '日韩视频的画质如何？', a: '所有日韩视频内容均提供高清画质（1080P），部分热门内容支持超高清4K分辨率，让您享受极致的观影体验。' },
              { q: '是否需要注册才能观看日韩视频？', a: '部分免费内容无需注册即可在线观看。注册会员可享受更多高清日韩视频资源和个性化推荐服务。' },
            ].map((faq, idx) => (
              <details key={idx} className="group bg-background-100 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer hover:bg-background-200/50 transition-colors">
                  <span className="font-medium text-foreground-900 text-sm pr-4">{faq.q}</span>
                  <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform text-lg w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                </summary>
                <div className="px-6 pb-4 text-sm text-foreground-600 leading-relaxed">
                  {faq.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}